import { arrayOf, shape, string } from 'prop-types';
import React, { Fragment } from 'react';
import { withRouter } from 'next/router';
import AmpHeader from 'components/AmpHeader';
import AmpFooter from 'components/AmpFooter';
import AmpOutbrain from 'components/AmpOutbrain';
import AmpSpread from 'components/AmpSpread';
import AmpAnalytics from 'components/AmpAnalytics';
import AmpStickyAd from 'components/AmpStickyAd';
import PostBundle from 'components/PostBundle';
import AmpSkimlinks from 'components/AmpSkimlinks';
import Metadata from 'components/Metadata';
import CoverMedia from 'components/CoverMedia';
import AmpGallery from 'components/AmpGallery';
import AmpImg from 'components/AmpImg';
import AmpVideo from 'components/AmpVideo';
import { getBundle } from 'util/bundles';
import { getCleanPath } from 'util/link';
import { getSiteHeaderSettings } from 'util/header';
import { HAS_RECIRC } from 'constants/site';
import { AMP_OUTBRAIN_WIDGET_IDS } from 'constants/vendor';
import { POST } from 'constants/pageTypes';
import {
  PERMUTIVE_PROJECT_ID,
  PERMUTIVE_API_KEY,
  PERMUTIVE_NAMESPACE
} from 'constants/ads/permutive';
import styles from './styles';

const COVER_MEDIA_COMPONENTS = {
  gallery: AmpGallery,
  image: AmpImg,
  Video: AmpVideo
};

const triggers = {
  teaser: {
    on: 'click',
    selector: '.AmpSpread__item--teaser a',
    request: 'event',
    vars: {
      eventCategory: 'Teaser',
      eventAction: 'Click',
      eventLabel: 'AMP'
    }
  }
};

export function AmpPostLayout(props) {
  const { pageBundles, contentBundles, contentStats, router } = props;

  const metadata = getBundle(pageBundles, 'metadata');

  const post = getBundle(contentBundles, 'post');
  const recirc = HAS_RECIRC ? getBundle(contentBundles, 'r') : null;

  const currentPath = router.asPath
    ? getCleanPath(router.asPath)
    : router.pathname;

  const { logo } = getSiteHeaderSettings(currentPath);

  return (
    <Fragment>
      {metadata && metadata.items && (
        <Fragment>
          <Metadata items={metadata.items} />
          <AmpAnalytics
            metadataItems={metadata.items}
            pageType={POST}
            triggers={triggers}
            contentStats={contentStats}
          />
        </Fragment>
      )}
      <AmpHeader logo={logo} />
      {/* For MVP we only want to render the post bundle */}
      <main className="AmpPostLayout">
        {post && (
          <Fragment>
            <amp-iframe
              width="1"
              height="1"
              style={{ position: 'absolute', top: '76vh' }}
              layout="fixed"
              sandbox="allow-scripts allow-same-origin"
              src={`https://${PERMUTIVE_NAMESPACE}.amp.permutive.com/amp-iframe.html?project=${PERMUTIVE_PROJECT_ID}&key=${PERMUTIVE_API_KEY}`}
            />
            <PostBundle
              {...post}
              coverMedia={
                post.data.coverMedia && (
                  <div className="AmpPostLayout__cover">
                    <CoverMedia
                      components={COVER_MEDIA_COMPONENTS}
                      type={post.data.coverMedia.type}
                      data={post.data.coverMedia.data}
                      post={{
                        ads: post.data.ads,
                        canonical: post.data.canonical,
                        title: post.data.title
                      }}
                    />
                  </div>
                )
              }
              // Comments are not supported in AMP
              comments={null}
              isAmp
            />
            {recirc && (
              <div className="AmpPostLayout__recirc">
                <AmpSpread {...recirc} />
              </div>
            )}
            <div className="AmpPostLayout__outbrain">
              <AmpOutbrain
                canonical={post.data.canonical}
                widgetIds={AMP_OUTBRAIN_WIDGET_IDS}
              />
            </div>
            {post.isAffiliate && <AmpSkimlinks />}
            {!(post.hideAds || post.isSponsored) && <AmpStickyAd post={post} />}
          </Fragment>
        )}
      </main>
      <AmpFooter />
      <style jsx global>
        {styles}
      </style>
    </Fragment>
  );
}

AmpPostLayout.defaultProps = {
  pageBundles: undefined,
  contentBundles: undefined,
  contentStats: undefined
};

AmpPostLayout.propTypes = {
  pageBundles: arrayOf(
    shape({
      layout: string.isRequired
    })
  ),
  contentBundles: arrayOf(
    shape({
      layout: string.isRequired
    })
  ),
  contentStats: shape({
    // TBD
  }),
  router: shape({
    pathname: string.isRequired,
    query: shape({}).isRequired,
    asPath: string.isRequired
  }).isRequired
};

export default withRouter(AmpPostLayout);
